/**
 * @file components/dispatch/CaseProgressSteps.tsx
 * @description 案件詳情頁頂部 6-step 進度條，兼任內容切換導航。
 *
 * 6 步：文件審核 → 合約評審 → 報價 → 繳費 → 派遣 → 已派案
 *   - 文件審核：受理建檔期間最早的申請文件審核 gate（documentReviewStatus 推導；
 *               報價/繳費 service 端會 assert 已過審）。
 *   - 合約評審：文件審核通過後的合約評審 gate（contractReviewState 推導；
 *               accept 後 workflowState → 合約已簽）。
 *
 * 由 workflowState + contractReviewState + documentReviewStatus 推導每步狀態（顯示進度色）：
 *   wait | process | finish | error
 *
 * 預派案（2026-09-11）：非「初次申請」案件可跳過前四步直接派遣（preDispatched=true）。
 * 這類案件雖已在「已派案」及之後，前四步仍是「待補辦」而非已完成，故不走
 * 「派案後全綠」捷徑，改按各步自身的真實狀態（文件審核 / 合約評審 / 有無報價 /
 * 有無到帳繳費）顯示，讓管理組一眼看出還欠哪幾步。
 *
 * 同時取代原本詳情頁底部的 Tabs：傳入 `activeStep` + `onStepChange`
 * 後，步驟條變為可點擊，點擊某步即切換下方對應內容面板，避免步驟條與
 * Tabs 兩層重複導航。
 */
import { Steps } from 'antd';

const STEP_TITLES = ['應附文件', '合約評審', '報價', '繳費', '派遣', '已派案'] as const;

export type StepStatus = 'wait' | 'process' | 'finish' | 'error';

export interface StepCtx {
  workflowState: string;
  contractReviewState:
    | 'pending'
    | 'in_review'
    | 'accepted'
    | 'rejected_fixable'
    | 'rejected_terminal';
  /** 最新申請文件審核狀態；null/未傳代表尚未提交審核。 */
  documentReviewStatus?: 'pending' | 'approved' | 'rejected' | null;
  /** 是否為預派案案件（cases.pre_dispatched_at 非 null）：前四步改為派案後補辦。 */
  preDispatched?: boolean;
  /** 是否已建立報價單（預派案補辦進度用）。 */
  hasQuotation?: boolean;
  /** 是否已有到帳（confirmedAt 非 null）的繳費紀錄（預派案補辦進度用）。 */
  hasConfirmedPayment?: boolean;
  /**
   * 是否具備預派案資格（驗證類型非初次申請，`isPreDispatchCase`）。
   * 尚未派案的合資格案件，其「派遣」步驟即刻可用（不必等前四步），故顯示為 process。
   */
  preDispatchEligible?: boolean;
}

/**
 * 由案件状态推导 6 步进度。纯函数，便于单测。
 *
 * 階段次序：受理建檔(文件審核 → 合約評審) → 合約已簽 → 已報價 → 已收費 → 已派案。
 * 文件審核為受理階段最早的 gate；通過後才輪到合約評審，合約 accept 後 → 合約已簽。
 */
export function getStepStates(ctx: StepCtx): StepStatus[] {
  const {
    workflowState,
    contractReviewState,
    documentReviewStatus,
    preDispatched,
    hasQuotation,
    hasConfirmedPayment,
    preDispatchEligible,
  } = ctx;

  // 已派案及之后：6 步全完成
  const POST_DISPATCH = new Set([
    '已派案',
    '已繳費',
    '稽核中',
    '報告中',
    '報告評審',
    '決定中',
    '已發證',
    '已寄送',
    '結案',
  ]);
  if (POST_DISPATCH.has(workflowState)) {
    if (!preDispatched) {
      return ['finish', 'finish', 'finish', 'finish', 'finish', 'finish'];
    }
    // 預派案：派遣／已派案已完成，前四步按自身真實狀態顯示（未完成＝process「待補辦」）
    return [
      documentReviewStatus === 'approved'
        ? 'finish'
        : documentReviewStatus === 'rejected'
          ? 'error'
          : 'process',
      contractReviewState === 'accepted'
        ? 'finish'
        : contractReviewState === 'rejected_fixable' || contractReviewState === 'rejected_terminal'
          ? 'error'
          : 'process',
      hasQuotation ? 'finish' : 'process',
      hasConfirmedPayment ? 'finish' : 'process',
      'finish',
      'finish',
    ];
  }

  // 第 1 步：文件審核。受理階段最早的 gate。
  let docReview: StepStatus;
  if (workflowState === '受理建檔') {
    docReview =
      documentReviewStatus === 'rejected'
        ? 'error'
        : documentReviewStatus === 'approved'
          ? 'finish'
          : 'process';
  } else if (workflowState === '合約已簽') {
    // 已進入合約已簽：若文件審核仍未過審，視為當前阻塞 gate（報價被擋）
    docReview =
      documentReviewStatus === 'rejected'
        ? 'error'
        : documentReviewStatus === 'approved'
          ? 'finish'
          : 'process';
  } else {
    docReview = 'finish'; // 已報價/已收費 ⇒ 文件審核必已通過（報價 gate）
  }

  // 第 2 步：合約評審。文件審核通過後才輪到；離開受理建檔即代表已通過。
  let contract: StepStatus;
  if (workflowState === '受理建檔') {
    if (docReview !== 'finish') {
      contract = 'wait'; // 文件審核尚未通過，未輪到合約評審
    } else {
      contract =
        contractReviewState === 'rejected_fixable' || contractReviewState === 'rejected_terminal'
          ? 'error'
          : contractReviewState === 'accepted'
            ? 'finish'
            : 'process';
    }
  } else {
    contract = 'finish';
  }

  // 第 3–5 步：報價 / 繳費 / 派遣，按 workflowState 推進
  let quote: StepStatus = 'wait';
  let pay: StepStatus = 'wait';
  let dispatch: StepStatus = 'wait';
  switch (workflowState) {
    case '合約已簽':
      // 文件審核通過後才輪到報價
      quote = docReview === 'finish' ? 'process' : 'wait';
      break;
    case '已報價':
      quote = 'finish';
      pay = 'process';
      break;
    case '已收費':
      quote = 'finish';
      pay = 'finish';
      dispatch = 'process';
      break;
    default:
      // 受理建檔 或 NC整改中 等非主線狀態：報價/繳費/派遣保持 wait
      break;
  }

  // 預派案資格（非初次申請）：前四步未完成也能直接派遣，故「派遣」步驟即刻可用。
  if (preDispatchEligible && ['受理建檔', '合約已簽', '已報價'].includes(workflowState)) {
    dispatch = 'process';
  }

  // 第 6 步：已派案，僅 POST_DISPATCH（上方已返回）才 finish，否則恆為 wait
  return [docReview, contract, quote, pay, dispatch, 'wait'];
}

/**
 * 由案件狀態推導「預設聚焦步驟索引」：
 * 取第一個 process 步驟；若全部完成（無 process）則取最後一步。
 * 詳情頁用它決定初次進入時預設展示哪個內容面板。
 */
export function getDefaultActiveStep(ctx: StepCtx): number {
  const states = getStepStates(ctx);
  const processIdx = states.indexOf('process');
  return processIdx === -1 ? states.length - 1 : processIdx;
}

export interface CaseProgressStepsProps extends StepCtx {
  /**
   * 當前聚焦（已選中）步驟索引。傳入後步驟條由「純進度展示」升級為
   * 「導航 + 進度展示」：current 跟隨選中項而非自動推導。
   */
  activeStep?: number;
  /** 點擊步驟時回呼，傳出被點擊步驟的索引；用於切換下方內容面板。 */
  onStepChange?: (index: number) => void;
}

export function CaseProgressSteps(props: CaseProgressStepsProps) {
  const { activeStep, onStepChange } = props;
  const states = getStepStates(props);
  // 導航模式下 current 跟隨選中步驟；純展示模式下回退到進度推導值。
  const current = activeStep ?? getDefaultActiveStep(props);
  const status = states.includes('error') ? 'error' : undefined;

  return (
    <Steps
      size="small"
      current={current}
      status={status}
      // 傳入 onStepChange 後 antd Steps 各步可點擊（游標變手型），實現面板切換。
      onChange={onStepChange}
      items={STEP_TITLES.map((title, i) => {
        const progress = states[i];
        // 導航模式下，被選中的步驟即使進度上仍是 wait（尚未推進到該階段），
        // 也要高亮為 process，讓使用者看到自己正停在哪一格；否則 antd Steps 會
        // 因 per-item status='wait' 覆蓋 current 而保持灰色（如點「繳費」不亮）。
        // finish / error 保留各自語意色（綠勾 / 紅），不被選中態覆蓋。
        const display = i === current && progress === 'wait' ? 'process' : progress;
        // 不傳 icon：antd Steps 預設以數字序號（1、2、3…）呈現各步。
        return {
          title,
          status: display,
        };
      })}
      style={{ marginBottom: 16 }}
    />
  );
}
